import { targetConsented } from '@tv2/ui-ng/libraries/tracking/adobeTargetHelpers';
import { withConsentStatus } from '@tv2/lib-consent/tracking';

export type AdobeEvents =
  | 'page view'
  | 'user interaction'
  | 'impression event'
  | 'cta clicked'
  | 'overlay displayed';

export type AdobeDataType = Record<string, any>;

type AdobeTrackingProps = {
  event: AdobeEvents | any;
  data: AdobeDataType;
  onDispatch?: (event: AdobeEvents, data: AdobeDataType) => void;
};
/**
 * @function adobeTracking() calls
 * @function adobe() with the data passed in, which calls
 * @function track() to track
 */
export async function adobeTracking({
  event,
  data,
  onDispatch,
}: AdobeTrackingProps): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  const { target, ...adobeData }: any = data ?? {};
  if (target && event === 'page view') {
    try {
      await targetConsented();
      adobeData.targetResponse = 'success';
    } catch (error: any) {
      // no Target (timeout or blocked) :(
      adobeData.targetResponse = error.message;
    }
  }

  await adobe(event, adobeData);
  if (typeof onDispatch === 'function') onDispatch(event, adobeData);
}

type AdobeWindow = Window &
  typeof globalThis & {
    DTMQ: any;
    dataLayer: Record<string, unknown>;
    _satellite: any;
  };

if (typeof window !== 'undefined') {
  (window as AdobeWindow).DTMQ = (window as AdobeWindow).DTMQ || [];
}

function adobe(event: string, data: Record<string, unknown>): Promise<void> {
  return new Promise((resolve, reject) => {
    (window as AdobeWindow).DTMQ.push(() => {
      if (event) {
        // Added here rather than by each caller, so no tracking event can forget it. Read at
        // dispatch time — which is when the queued callback runs — so the value reflects the
        // consent in force when the event actually leaves, not when it was queued.
        // consentStatus is always present (accepted, declined or notDecided); consentDetails
        // once the Cookiebot provider has recorded it. Deliberately not the advid — an
        // identifier does not belong on the tracking payload; Adobe only needs to know whether
        // consent was given to govern downstream data use.
        trackWithConsent(event, data);
      }

      resolve();
    });

    setTimeout(() => {
      reject();
    }, 2000);
  });
}

const trackWithConsent = withConsentStatus(track);

function track(event: string, data: Record<string, unknown>) {
  const w = window as AdobeWindow;
  // TODO: Remove when eCapacity has implemented window.dataLayer updates internally:
  if (event === 'page view') {
    w.dataLayer = data;
  }
  setTimeout(() => {
    w._satellite.track(event, data);
  });

  if (process.env.BUILD_FLAG_DTM_DEBUG === 'true') {
    // biome-ignore lint/suspicious/noConsole: Intentional console usage
    console.log('🌍 Adobe Tracking:', event, data);
    /* eslint-enable no-console */
  }
}

// TODO: remove Once LandingPageLayout is moved.
export async function legacyAdobeTracking(
  event: any = undefined,
  data: any = undefined,
  onDispatch: any = undefined
) {
  if (typeof window === 'undefined') return Promise.resolve();

  const { target, ...adobeData }: any = data ?? {};
  if (target && event === 'page view') {
    try {
      await targetConsented();
      adobeData.targetResponse = 'success';
    } catch (error: any) {
      // no Target (timeout or blocked) :(
      adobeData.targetResponse = error.message;
    }
  }

  await adobe(event, adobeData);
  if (typeof onDispatch === 'function') onDispatch(event, adobeData);
}
